import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./participante.css";

import Form from "react-bootstrap/Form";

import useMenu from "../../hooks/useMenuContext";
import useAuth from "../../hooks/useAuthContext";
import api from "../../services/axios";
import ItemParticipantes from "../../components/participantes/itemParticipante";
import { FaSpinner, FaFilePdf, FaSearch, FaUsers, FaArrowLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { abrirDossieConvidadosEmNovaAba } from "../../services/dossieConvidadosApi";

const FILTROS = [
  { id: "Nome", label: "Nome", tipo: "text", placeholder: "Buscar por nome" },
  {
    id: "Empresa",
    label: "Empresa",
    tipo: "text",
    placeholder: "Buscar por empresa",
  },
  {
    id: "Cargo",
    label: "Cargo",
    tipo: "select",
    placeholder: "Filtrar por cargo",
  },
  {
    id: "Tipo Instituição",
    label: "Instituição",
    tipo: "select",
    placeholder: "Filtrar por instituição",
  },
];

const OPTIONS_CARGOS = [
  { value: "", label: "Selecione um cargo" },
  { value: "Presidente", label: "Presidente" },
  { value: "CEO", label: "CEO" },
  { value: "CIO_CMO_COO", label: "CIO, CMO, COO" },
  { value: "Diretor Executivo", label: "Diretor Executivo" },
  { value: "Superintendente", label: "Superintendente" },
];

const OPTIONS_INSTITUICOES = [
  { value: "", label: "Selecione uma instituição" },
  { value: "Hospitais", label: "Hospitais" },
  { value: "Laboratórios", label: "Laboratórios" },
  { value: "Clinicas", label: "Clínicas" },
  { value: "Operadoras", label: "Operadoras" },
  { value: "Outros", label: "Outros" },
];

const TIPOS_LISTAGEM = [
  "convidado",
  "patrocinador",
  "administracao_dash",
  "participante_patrocinador",
];

const FILTROS_TIPO = [
  { id: "todos", label: "Todos" },
  { id: "convidado", label: "Convidado" },
  { id: "patrocinador", label: "Patrocinador" },
];

const TIPOS_POR_FILTRO = {
  todos: TIPOS_LISTAGEM,
  convidado: ["convidado"],
  patrocinador: ["patrocinador", "participante_patrocinador"],
};

const Participantes = () => {
  const navigate = useNavigate();
  const { menuIsOpen } = useMenu();
  const { usuario } = useAuth();
  const [participantes, setParticipantes] = useState([]);
  const [isBaixandoDossie, setIsBaixandoDossie] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState("");
  const [labelPesquisa, setLabelPesquisa] = useState("Nome");
  const [filtroTipo, setFiltroTipo] = useState("todos");
  const debounceRef = useRef(null);

  const filtroAtivo = useMemo(
    () => FILTROS.find((f) => f.id === labelPesquisa) || FILTROS[0],
    [labelPesquisa]
  );

  const optionsSelect = useMemo(() => {
    if (labelPesquisa === "Cargo") return OPTIONS_CARGOS;
    if (labelPesquisa === "Tipo Instituição") return OPTIONS_INSTITUICOES;
    return [];
  }, [labelPesquisa]);

  const montarPayload = useCallback(
    (filtroTxt, campo, tipoSelecionado = "todos") => {
      const valor = (filtroTxt || "").trim();
      const dados = {
        tipo: TIPOS_POR_FILTRO[tipoSelecionado] || TIPOS_LISTAGEM,
      };

      if (!valor || campo === "Todos") {
        return dados;
      }

      switch (campo) {
        case "Nome":
          dados.nome = valor;
          break;
        case "Empresa":
          dados.empresa = valor;
          break;
        case "Cargo":
          dados.cargo = valor;
          break;
        case "Tipo Instituição":
          dados.instituicao = valor;
          break;
        default:
          break;
      }

      return dados;
    },
    []
  );

  const buscarParticipantes = useCallback(
    async (filtroTxt, campo, tipoSelecionado = filtroTipo) => {
      setLoading(true);
      try {
        const respUsuarios = await api.post(
          `/usuarios-apenas-user-externo`,
          montarPayload(filtroTxt, campo, tipoSelecionado)
        );

        if (respUsuarios.status === 200) {
          setParticipantes(respUsuarios.data.dados || []);
        }
      } catch (err) {
        console.error(err);
        toast.error("Não foi possível carregar os participantes.");
      } finally {
        setLoading(false);
      }
    },
    [filtroTipo, montarPayload]
  );

  useEffect(() => {
    buscarParticipantes("", "Todos", "todos");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- carga inicial
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleUsuarios = (
    filtroTxt,
    campo = labelPesquisa,
    imediato = false,
    tipoSelecionado = filtroTipo
  ) => {
    setFiltro(filtroTxt);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    const executar = () =>
      buscarParticipantes(filtroTxt, campo, tipoSelecionado);

    if (imediato || filtroAtivo.tipo === "select") {
      executar();
      return;
    }

    debounceRef.current = setTimeout(executar, 320);
  };

  const handleTrocaFiltro = (novoFiltro) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setFiltro("");
    setLabelPesquisa(novoFiltro.id);
    buscarParticipantes("", "Todos", filtroTipo);
  };

  const handleTrocaTipo = (novoTipo) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    setFiltroTipo(novoTipo);
    const campoBusca = filtro.trim() ? labelPesquisa : "Todos";
    buscarParticipantes(filtro, campoBusca, novoTipo);
  };

  const podeBaixarDossie = usuario?.tipo === "administracao";

  const handleBaixarDossie = async () => {
    setIsBaixandoDossie(true);
    try {
      const dados = await abrirDossieConvidadosEmNovaAba();
      toast.success(
        dados?.emCache
          ? `Dossiê em cache (${dados.totalConvidados} convidados).`
          : `Dossiê gerado com ${dados?.totalConvidados ?? 0} convidados.`
      );
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.mensagem || "Erro ao baixar dossiê PDF.");
    } finally {
      setIsBaixandoDossie(false);
    }
  };

  const totalLabel =
    participantes.length === 1
      ? "1 pessoa"
      : `${participantes.length} pessoas`;

  return (
    <div className="main participantes-page">
      <div className="corpo-pagina pp-corpo">
        {!menuIsOpen && (
          <header className="pp-header">
            <div className="pp-header-top">
              <div className="pp-titulo-wrap">
                <h1 className="pp-titulo">Participantes</h1>
                <p className="pp-subtitulo">
                  Encontre convidados e inicie uma conversa
                </p>
              </div>
              <div className="pp-header-actions">
                {podeBaixarDossie ? (
                  <button
                    type="button"
                    className="pp-btn-dossie"
                    onClick={handleBaixarDossie}
                    disabled={isBaixandoDossie}
                  >
                    {isBaixandoDossie ? (
                      <FaSpinner className="fa-spin" aria-hidden />
                    ) : (
                      <FaFilePdf aria-hidden />
                    )}
                    <span>Dossiê PDF</span>
                  </button>
                ) : null}
                <button
                  type="button"
                  className="pp-btn-voltar"
                  onClick={() => navigate(-1)}
                  title="Voltar"
                >
                  <FaArrowLeft aria-hidden />
                  <span>Voltar</span>
                </button>
              </div>
            </div>

            <div className="pp-search-shell">
              <div className="pp-search-field">
                <FaSearch className="pp-search-icon" aria-hidden />
                {filtroAtivo.tipo === "text" ? (
                  <Form.Control
                    className="pp-search-input"
                    aria-label={filtroAtivo.placeholder}
                    placeholder={filtroAtivo.placeholder}
                    onChange={(e) => handleUsuarios(e.target.value)}
                    value={filtro}
                  />
                ) : (
                  <Form.Select
                    className="pp-search-input pp-search-select"
                    aria-label={filtroAtivo.placeholder}
                    onChange={(e) =>
                      handleUsuarios(e.target.value, labelPesquisa, true)
                    }
                    value={filtro}
                  >
                    {optionsSelect.map((opt) => (
                      <option key={opt.value || "all"} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </div>

              <div className="pp-filtros-linha">
                <div
                  className="pp-chips"
                  role="tablist"
                  aria-label="Campo de busca"
                >
                  {FILTROS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      aria-selected={labelPesquisa === item.id}
                      className={`pp-chip ${
                        labelPesquisa === item.id ? "is-active" : ""
                      }`}
                      onClick={() => handleTrocaFiltro(item)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>

                <div
                  className="pp-tipo-linha"
                  role="tablist"
                  aria-label="Filtrar por tipo"
                >
                  {FILTROS_TIPO.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      aria-selected={filtroTipo === item.id}
                      className={`pp-chip pp-chip--tipo ${
                        item.id !== "todos" ? `pp-chip--${item.id}` : ""
                      } ${filtroTipo === item.id ? "is-active" : ""}`}
                      onClick={() => handleTrocaTipo(item.id)}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>

                <span className="pp-meta-count">
                  <FaUsers aria-hidden />
                  {loading ? "Buscando..." : totalLabel}
                </span>
              </div>
            </div>
          </header>
        )}

        <div id="lista-participantes" className="pp-lista">
          {loading ? (
            <div className="pp-estado" role="status">
              <FaSpinner className="fa-spin pp-estado-icon" aria-hidden />
              <p>Carregando participantes...</p>
            </div>
          ) : participantes.length === 0 ? (
            <div className="pp-estado">
              <FaUsers className="pp-estado-icon" aria-hidden />
              <p className="pp-estado-titulo">Ninguém encontrado</p>
              <p className="pp-estado-texto">
                Ajuste a busca ou os filtros para encontrar alguém para
                conversar.
              </p>
            </div>
          ) : (
            participantes.map((participante) => (
              <ItemParticipantes
                key={participante.id}
                participante={participante}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Participantes;
